import React from 'react';
import SafeAvatar from '../SafeAvatar';
import { AVAILABLE_AREAS, AVAILABLE_ROLES } from './constants';

const getFullName = (member) => {
  if (member.firstName || member.lastName) {
    return `${member.firstName || ''} ${member.lastName || ''}`.trim();
  }
  return member.name || 'Unnamed Team Member';
};

const getInitials = (member) => {
  const first = member.firstName?.[0] || member.name?.[0] || '';
  const last = member.lastName?.[0] || '';
  return `${first}${last}`.toUpperCase() || '?';
};

const getRoleLabel = (role) => (
  AVAILABLE_ROLES.find((availableRole) => availableRole.value === role)?.label || role || 'Team Member'
);

const getRoleColors = (role) => {
  const roleColors = {
    ADMIN: 'bg-red-50 text-red-700 ring-red-600/20',
    MANAGER: 'bg-purple-50 text-purple-700 ring-purple-600/20',
    GUEST_APP_MANAGER: 'bg-indigo-50 text-indigo-700 ring-indigo-600/20',
    PROPERTY_MANAGER: 'bg-blue-50 text-blue-700 ring-blue-600/20',
    HOUSEKEEPER_MANAGER: 'bg-green-50 text-green-700 ring-green-600/20',
    HOUSEKEEPER: 'bg-emerald-50 text-emerald-700 ring-emerald-600/20',
    INSPECTOR: 'bg-orange-50 text-orange-700 ring-orange-600/20',
    MAINTENANCE: 'bg-yellow-50 text-yellow-700 ring-yellow-600/20',
    CALL_CENTER: 'bg-pink-50 text-pink-700 ring-pink-600/20'
  };
  return roleColors[role] || 'bg-gray-50 text-gray-700 ring-gray-600/20';
};

const getAreas = (member) => (
  (member.areas || [])
    .map((area) => AVAILABLE_AREAS.find((availableArea) => availableArea.value === area)?.label || area)
);

const getTeams = (member) => {
  if (member.userTeams?.length) {
    return member.userTeams
      .filter((userTeam) => userTeam.team)
      .map((userTeam) => ({
        id: userTeam.id,
        name: userTeam.team.name,
        color: userTeam.team.color,
        isPrimary: userTeam.isPrimary
      }));
  }

  if (member.team) {
    return [{
      id: member.team.id,
      name: member.team.name,
      color: member.team.color,
      isPrimary: true
    }];
  }

  return [];
};

const TeamBadge = ({ team }) => (
  <span
    className="inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs font-medium"
    style={{
      backgroundColor: team.color ? `${team.color}1F` : '#F3F4F6',
      borderColor: team.color || '#D1D5DB',
      color: team.color || '#374151'
    }}
  >
    <span
      className="h-1.5 w-1.5 rounded-full"
      style={{ backgroundColor: team.color || '#6B7280' }}
    />
    {team.name}
    {team.isPrimary && (
      <span className="font-semibold" title="Primary team">
        Primary
      </span>
    )}
  </span>
);

const ContactLink = ({ href, children }) => {
  if (!href) {
    return <span className="text-gray-500">{children}</span>;
  }

  return (
    <a
      href={href}
      onClick={(event) => event.stopPropagation()}
      className="text-sky-700 hover:text-sky-900 hover:underline"
    >
      {children}
    </a>
  );
};

function TeamMemberDirectoryCard({
  member,
  activeTab,
  onViewProfile,
  canManageTeamMembers,
  onEdit,
  onToggleActive,
  onResendInvite,
  isResendingInvite,
  resendingMemberId
}) {
  const fullName = getFullName(member);
  const areas = getAreas(member);
  const teams = getTeams(member);
  const phoneHref = member.phoneNumber ? `tel:${member.phoneNumber.replace(/[^\d+]/g, '')}` : null;

  const handleCardKeyDown = (event) => {
    if (event.target !== event.currentTarget) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onViewProfile(member);
    }
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onViewProfile(member)}
      onKeyDown={handleCardKeyDown}
      className="w-full cursor-pointer rounded-lg border border-gray-200 bg-white p-4 text-left shadow-sm transition hover:border-sky-200 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-sky-500"
    >
      <div className="flex gap-4">
        <div className="relative flex-shrink-0">
          {member.avatarUrl ? (
            <SafeAvatar
              src={member.avatarUrl}
              alt={fullName}
              className="h-16 w-16 rounded-full ring-2 ring-white shadow-sm"
              size={64}
            />
          ) : (
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-sky-50 text-lg font-semibold text-sky-700 ring-1 ring-sky-100">
              {getInitials(member)}
            </span>
          )}
          <span
            className={`absolute bottom-0 right-0 h-3.5 w-3.5 rounded-full border-2 border-white ${
              member.isActive ? 'bg-green-500' : 'bg-gray-400'
            }`}
            title={member.isActive ? 'Active' : 'Inactive'}
          />
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0">
              <h3 className="truncate text-base font-semibold text-gray-900">{fullName}</h3>
              <div className="mt-1 flex flex-wrap items-center gap-2">
                <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${getRoleColors(member.role)}`}>
                  {getRoleLabel(member.role)}
                </span>
                {!member.isActive && (
                  <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700">
                    Inactive
                  </span>
                )}
              </div>
            </div>

            {canManageTeamMembers && (
              <div className="flex flex-shrink-0 gap-2" onClick={(event) => event.stopPropagation()}>
                <button
                  type="button"
                  onClick={() => onEdit(member)}
                  className="inline-flex items-center justify-center rounded-md border border-sky-200 bg-sky-50 px-3 py-1.5 text-xs font-medium text-sky-700 transition hover:bg-sky-100"
                >
                  Edit
                </button>
                {activeTab === 'inactive' ? (
                  <button
                    type="button"
                    onClick={() => onToggleActive(member)}
                    className="inline-flex items-center justify-center rounded-md border border-green-200 bg-green-50 px-3 py-1.5 text-xs font-medium text-green-700 transition hover:bg-green-100"
                  >
                    Reactivate
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => onResendInvite(member)}
                    disabled={isResendingInvite && resendingMemberId === member.id}
                    className="inline-flex items-center justify-center rounded-md border border-orange-200 bg-orange-50 px-3 py-1.5 text-xs font-medium text-orange-700 transition hover:bg-orange-100 disabled:cursor-not-allowed disabled:border-gray-200 disabled:bg-gray-50 disabled:text-gray-400"
                  >
                    {isResendingInvite && resendingMemberId === member.id ? 'Sending...' : 'Resend'}
                  </button>
                )}
              </div>
            )}
          </div>

          <div className="mt-3 grid gap-2 text-sm text-gray-600 sm:grid-cols-2">
            <div className="min-w-0 truncate">
              <span className="font-medium text-gray-500">Email: </span>
              <ContactLink href={`mailto:${member.email}`}>{member.email || 'No email'}</ContactLink>
            </div>
            <div className="min-w-0 truncate">
              <span className="font-medium text-gray-500">Phone: </span>
              <ContactLink href={phoneHref}>{member.phoneNumber || 'No phone listed'}</ContactLink>
            </div>
          </div>

          <div className="mt-3 flex flex-wrap gap-1.5">
            {areas.length ? (
              areas.slice(0, 4).map((area) => (
                <span key={area} className="inline-flex items-center rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700">
                  {area}
                </span>
              ))
            ) : (
              <span className="text-xs text-gray-500">No areas assigned</span>
            )}
            {areas.length > 4 && (
              <span className="text-xs text-gray-500">+{areas.length - 4} more</span>
            )}
          </div>

          {teams.length > 0 && (
            <div className="mt-3">
              <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Teams</p>
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {teams.map((team) => (
                  <TeamBadge key={team.id} team={team} />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function TeamMembersList({
  teamMembers,
  activeTab,
  onEdit,
  onToggleActive,
  onResendInvite,
  isResendingInvite,
  resendingMemberId,
  searchTerm,
  roleFilter,
  onClearFilters,
  onOpenCreateModal,
  onViewProfile,
  canManageTeamMembers = false
}) {
  if (teamMembers.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="text-center py-12">
          <svg
            className="mx-auto h-12 w-12 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
            />
          </svg>
          {searchTerm || roleFilter !== 'ALL' ? (
            <>
              <h3 className="mt-2 text-sm font-medium text-gray-900">No matching team members</h3>
              <p className="mt-1 text-sm text-gray-500">
                Try adjusting your search or filter criteria
              </p>
              <div className="mt-6">
                <button
                  onClick={onClearFilters}
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
                >
                  <svg className="-ml-1 mr-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                  Clear Filters
                </button>
              </div>
            </>
          ) : (
            <>
              <h3 className="mt-2 text-sm font-medium text-gray-900">No team members</h3>
              <p className="mt-1 text-sm text-gray-500">
                {canManageTeamMembers
                  ? 'Get started by creating a new team member.'
                  : 'No team members are available in this view.'}
              </p>
              {canManageTeamMembers && (
                <div className="mt-6">
                  <button
                    onClick={onOpenCreateModal}
                    className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-sky-600 hover:bg-sky-700"
                  >
                    <svg
                      className="-ml-1 mr-2 h-5 w-5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                      />
                    </svg>
                    Add Team Member
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 gap-3 xl:grid-cols-2">
        {teamMembers.map((member) => (
          <TeamMemberDirectoryCard
            key={member.id}
            member={member}
            activeTab={activeTab}
            onViewProfile={onViewProfile}
            canManageTeamMembers={canManageTeamMembers}
            onEdit={onEdit}
            onToggleActive={onToggleActive}
            onResendInvite={onResendInvite}
            isResendingInvite={isResendingInvite}
            resendingMemberId={resendingMemberId}
          />
        ))}
      </div>
    </div>
  );
}
